import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import OngoingSpellProgress from '../../admissions/ongoingSpellProgress';
import clsx from 'clsx';
import makeStyles from '@mui/styles/makeStyles';
import EdTimelineRow from './components/edTimelineRow';
import IpTimelineRow from './components/ipTimelineRow';
import RowContainer from './components/rowContainer';
import {useTranslation} from 'react-i18next';
import {getConfig} from '../../../util/configUtil';
import {getAdmissionStartDate} from '../../../util/patientUtil';
import moment from 'moment';
import React from 'react';
import _ from 'lodash';

export const useTimelineStyles = makeStyles((theme) => ({
    root: {
        alignItems: 'flex-start',
        color: '#626466',
        margin: 0,
        padding: 0,
        marginLeft: -5,
    },
    connector: {
        borderStyle: 'dashed',
        borderWidth: 1,
        backgroundColor: 'transparent',
    },
    content: {
        paddingTop: 7,
        '&.margin': {
            marginTop: -5,
        },
    },
    dot: {
        width: 24,
        height: 24,
        borderWidth: 3,
        '&.active': {
            borderColor: '#4EAC6F',
            borderWidth: 5,
        },
        '&.small': {
            width: 12,
            height: 12,
        },
    },
    tinySeparator: {
        margin: '0 5px',
    },
    divRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
    },
    smallText: {
        fontSize: 'small',
    },
    discriminator: {
        borderRadius: '50%',
        width: 14,
        height: 14,
    },
    avatarCustom: {
        width: 'auto !important',
        paddingLeft: 10,
        paddingRight: 10,
        borderRadius: 15,
        backgroundColor: theme.palette.background.paper,
        color: '#fff',
    },
    chipEpisode: {
        border: 'solid 1px #ffffff',
        borderRadius: 20,
        padding: '6px 12px',
        fontSize: 14,
    },
    inpatientSeparator: {
        marginBottom: 10,
    },
    headerText: {
        color: theme.palette.admissionForm.labelColor,
    },
}));

const AttendanceTimelineHistory = ({admission, expanded, review}) => {
    const {t} = useTranslation();

    // Config
    const dateFormat = getConfig('defaultDateTimeFormat');

    const classes = useTimelineStyles();
    const isActive = !admission?.admission?.endDate;
    const isED = admission?.admission?.attendanceType === 'E';

    const getArrival = () => {
        const date = getAdmissionStartDate(admission?.admission);
        return `${t(isED ? 'Arrival' : 'Admission')}: ${date.format(dateFormat)}`;
    };

    const getDischarged = () => {
        if (expanded) {
            const discharge = _.get(admission, 'studies.discharge');
            return (
                <div className={classes.divRow} style={{width: '100%'}}>
                    <div style={{width: '20%'}}>
                        <div>
                            {t(isED ? 'Departed' : 'Discharged')}
                        </div>

                        <div className={classes.smallText}>
                            {moment(admission?.admission?.endDate).format(dateFormat)}
                        </div>
                    </div>

                    <div className={classes.divRow} style={{width: '80%', gap: 20}}>
                        <div>
                            <div className={classes.smallText}>
                                {t('Discharge Status')}
                            </div>

                            <div>
                                {discharge?.dischargeStatus?.__description || t('Not set')}
                            </div>
                        </div>

                        <div>
                            <div className={classes.smallText}>
                                {t('Discharge Follow Up')}
                            </div>

                            <div>
                                {discharge?.dischargeFollowUp?.__description || t('Not set')}
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
        return `${isED ? t('Departed') : t('Discharged')}: ${moment(admission?.admission?.endDate).format(dateFormat)}`;
    };

    return (
        <Timeline className={classes.root}>
            <TimelineItem style={{marginTop: review ? 20 : (!isActive ? 10 : 0)}}>
                <TimelineSeparator>
                    <TimelineDot
                        variant={isActive ? 'outlined' : 'filled'}
                        className={clsx(classes.dot, isActive && 'active')}
                    />

                    <TimelineConnector className={classes.connector}/>
                </TimelineSeparator>

                <TimelineContent
                    className={clsx(classes.content, (expanded || !isActive) && 'margin')}
                    style={{marginBottom: (expanded && isED && !isActive) ? 22 : 0}}
                >
                    {isActive && <OngoingSpellProgress
                        label={`${t('Active')} (${admission?.areaDescription || '-'} / ${admission?.bedDescription || '-'})`}
                    />}

                    {!isActive &&
                        <RowContainer>
                            {getDischarged()}
                        </RowContainer>
                    }
                </TimelineContent>
            </TimelineItem>

            {expanded &&
                <>
                    {isED && <EdTimelineRow admission={admission}/>}
                    {!isED && <IpTimelineRow admission={admission}/>}
                </>
            }

            {!expanded &&
                <TimelineItem>
                    <TimelineSeparator>
                        <TimelineDot className={classes.dot} variant="outlined"/>
                    </TimelineSeparator>

                    <TimelineContent className={classes.fullWidthContent}>
                        <RowContainer>
                            {getArrival()}
                        </RowContainer>
                    </TimelineContent>
                </TimelineItem>
            }
        </Timeline>
    );
};
export default AttendanceTimelineHistory;